// This file formats data for display
// Like turning dates into readable format, formatting numbers, etc.

/**
 * 
 * NOTES-SUMMARY
 * 
 * What this file does:
 *  - Formats dates in readable ways
 *  - Formats numbers with commas
 *  - Formats percentages
 *  - Truncates long text
 *  - Masks email addresses

 *Why we need this:
 *  Raw data looks ugly. "2025-01-24T10:30:00.000Z" vs "Jan 24, 2025" - which is easier to read?

 */



/**
 * Format date to readable string
 * Example: "2025-01-24T10:30:00Z" becomes "Jan 24, 2025"
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format date to include time
 * Example: "2025-01-24T10:30:00Z" becomes "Jan 24, 2025 at 10:30 AM"
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Format relative time
 * Example: "2025-01-20T10:30:00Z" becomes "4 days ago"
 */
export const formatRelativeTime = (dateString) => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

/**
 * Format number with commas
 * Example: 1234567 becomes "1,234,567"
 */
export const formatNumber = (num) => {
  if (!num && num !== 0) return 'N/A';
  return num.toLocaleString('en-US');
};

/**
 * Format percentage
 * Example: 0.1834 becomes "18.3%"
 */
export const formatPercent = (num) => {
  if (!num && num !== 0) return 'N/A';
  return `${(num * 100).toFixed(1)}%`;
};

/**
 * Truncate text to max length
 * Example: "This is a very long sentence..." becomes "This is a very..."
 */
export const truncate = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Format email to hide part of it
 * Example: "john.doe@example.com" becomes "j****@example.com"
 */
export const maskEmail = (email) => {
  if (!email) return '';
  const [name, domain] = email.split('@');
  const maskedName = name.charAt(0) + '****';
  return `${maskedName}@${domain}`;
};